import React, { useEffect, useState } from 'react';
import authService from '../services/authService';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const BookingManager = ({ club, user }) => {
  const [config, setConfig] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTimeSlot, setSelectedTimeSlot] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [configForm, setConfigForm] = useState({
    startTime: '09:00',
    endTime: '21:00',
    slotDuration: 60,
    workingDays: [1, 2, 3, 4, 5], // Monday to Friday
    holidays: []
  });
  const [bookingNotes, setBookingNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (club) {
      loadData();
    }
  }, [club]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [configResult, bookingsResult] = await Promise.all([
        authService.authenticatedFetch(`${API_URL}/clubs/${club.id}/booking-config`),
        authService.authenticatedFetch(
          `${API_URL}/clubs/${club.id}/bookings?startDate=${getMonthStart()}&endDate=${getMonthEnd()}`
        )
      ]);

      if (configResult.success) {
        setConfig(configResult.data);
        setConfigForm({
          startTime: configResult.data.startTime,
          endTime: configResult.data.endTime,
          slotDuration: configResult.data.slotDuration,
          workingDays: configResult.data.workingDays.split(',').map(d => parseInt(d)),
          holidays: configResult.data.holidays || []
        });
      }

      if (bookingsResult.success) {
        setBookings(bookingsResult.data);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMonthStart = () => {
    const date = new Date(currentMonth);
    return new Date(date.getFullYear(), date.getMonth(), 1).toISOString().split('T')[0];
  };

  const getMonthEnd = () => {
    const date = new Date(currentMonth);
    return new Date(date.getFullYear(), date.getMonth() + 1, 0).toISOString().split('T')[0];
  };

  const handleConfigSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const result = await authService.authenticatedFetch(
        `${API_URL}/clubs/${club.id}/booking-config`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            startTime: configForm.startTime,
            endTime: configForm.endTime,
            slotDuration: parseInt(configForm.slotDuration),
            workingDays: configForm.workingDays.join(','),
            holidays: JSON.stringify(configForm.holidays)
          })
        }
      );

      if (result.success) {
        await loadData();
        setShowConfigModal(false);
      } else {
        alert(result.message || 'Error updating configuration');
      }
    } catch (error) {
      console.error('Error updating configuration:', error);
      alert('Error updating configuration');
    } finally {
      setSubmitting(false);
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const result = await authService.authenticatedFetch(
        `${API_URL}/bookings`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            clubId: club.id,
            userId: user.id,
            bookingDate: selectedDate.toISOString().split('T')[0],
            startTime: selectedTimeSlot.start,
            endTime: selectedTimeSlot.end,
            notes: bookingNotes
          })
        }
      );

      if (result.success) {
        await loadData();
        setShowBookingModal(false);
        setSelectedDate(null);
        setSelectedTimeSlot(null);
        setBookingNotes('');
      } else {
        alert(result.message || 'Error creating booking');
      }
    } catch (error) {
      console.error('Error creating booking:', error);
      alert('Error creating booking');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleWorkingDay = (day) => {
    setConfigForm(prev => ({
      ...prev,
      workingDays: prev.workingDays.includes(day)
        ? prev.workingDays.filter(d => d !== day)
        : [...prev.workingDays, day].sort()
    }));
  };

  const addHoliday = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    if (!configForm.holidays.includes(dateStr)) {
      setConfigForm(prev => ({
        ...prev,
        holidays: [...prev.holidays, dateStr].sort()
      }));
    }
  };

  const removeHoliday = (dateStr) => {
    setConfigForm(prev => ({
      ...prev,
      holidays: prev.holidays.filter(h => h !== dateStr)
    }));
  };

  const generateTimeSlots = () => {
    if (!config) return [];
    
    const slots = [];
    const [startHour, startMin] = config.startTime.split(':').map(Number);
    const [endHour, endMin] = config.endTime.split(':').map(Number);
    
    let currentTime = startHour * 60 + startMin;
    const endTime = endHour * 60 + endMin;
    
    while (currentTime < endTime) {
      const nextTime = currentTime + config.slotDuration;
      const startH = Math.floor(currentTime / 60).toString().padStart(2, '0');
      const startM = (currentTime % 60).toString().padStart(2, '0');
      const endH = Math.floor(nextTime / 60).toString().padStart(2, '0');
      const endM = (nextTime % 60).toString().padStart(2, '0');
      
      slots.push({
        start: `${startH}:${startM}`,
        end: `${endH}:${endM}`
      });
      
      currentTime = nextTime;
    }
    
    return slots;
  };

  const isSlotBooked = (date, slot) => {
    const dateStr = date.toISOString().split('T')[0];
    return bookings.some(booking => {
      const bookingDateStr = new Date(booking.bookingDate).toISOString().split('T')[0];
      return bookingDateStr === dateStr &&
        booking.status === 'confirmed' &&
        booking.startTime === slot.start &&
        booking.endTime === slot.end;
    });
  };

  const isWorkingDay = (date) => {
    if (!config) return false;
    const dayOfWeek = date.getDay();
    return configForm.workingDays.includes(dayOfWeek);
  };

  const isHoliday = (date) => {
    const dateStr = date.toISOString().split('T')[0];
    return configForm.holidays.includes(dateStr);
  };

  const getDaysInMonth = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(null);
    }
    
    // Add all days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(year, month, day));
    }
    
    return days;
  };

  const handleDateClick = (date) => {
    if (!date || !isWorkingDay(date) || isHoliday(date)) return;
    setSelectedDate(date);
    setShowBookingModal(true);
  };

  const cancelBooking = async (bookingId) => {
    if (!window.confirm('Are you sure you want to cancel this booking?')) return;

    try {
      const result = await authService.authenticatedFetch(
        `${API_URL}/bookings/${bookingId}/cancel`,
        { method: 'PUT' }
      );

      if (result.success) {
        await loadData();
      } else {
        alert(result.message || 'Error cancelling booking');
      }
    } catch (error) {
      console.error('Error cancelling booking:', error);
      alert('Error cancelling booking');
    }
  };

  const prevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-8">
        <div className="text-center text-gray-500">Loading booking configuration...</div>
      </div>
    );
  }

  const daysInMonth = getDaysInMonth();
  const monthName = currentMonth.toLocaleString('default', { month: 'long', year: 'numeric' });
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="space-y-6">
      {/* Configuration Section */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-5 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Booking Configuration</h3>
            <p className="mt-1 text-sm text-gray-500">
              Configure booking times and availability for {club.name}
            </p>
          </div>
          <button
            onClick={() => setShowConfigModal(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
          >
            <svg className="h-5 w-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            Configure
          </button>
        </div>
        
        {config && (
          <div className="p-6 grid grid-cols-3 gap-6">
            <div>
              <p className="text-sm font-medium text-gray-500">Operating Hours</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {config.startTime} - {config.endTime}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Slot Duration</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {config.slotDuration} minutes
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Working Days</p>
              <p className="mt-1 text-lg font-semibold text-gray-900">
                {configForm.workingDays.map(d => ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d]).join(', ')}
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Calendar Section */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-5 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">{monthName}</h3>
            <div className="flex space-x-2">
              <button
                onClick={prevMonth}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                onClick={nextMonth}
                className="p-2 rounded-md hover:bg-gray-100"
              >
                <svg className="h-5 w-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="grid grid-cols-7 gap-2 mb-2">
            {weekDays.map(day => (
              <div key={day} className="text-center text-sm font-semibold text-gray-700 py-2">
                {day}
              </div>
            ))}
          </div>
          
          <div className="grid grid-cols-7 gap-2">
            {daysInMonth.map((date, index) => {
              if (!date) {
                return <div key={`empty-${index}`} className="aspect-square" />;
              }
              
              const isWorking = isWorkingDay(date);
              const isHol = isHoliday(date);
              const dateStr = date.toISOString().split('T')[0];
              const dayBookings = bookings.filter(b => 
                new Date(b.bookingDate).toISOString().split('T')[0] === dateStr &&
                b.status === 'confirmed'
              );
              
              return (
                <button
                  key={date.toISOString()}
                  onClick={() => handleDateClick(date)}
                  disabled={!isWorking || isHol}
                  className={`aspect-square p-2 rounded-lg border-2 transition-colors ${
                    !isWorking || isHol
                      ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                      : dayBookings.length > 0
                      ? 'bg-indigo-50 border-indigo-300 hover:bg-indigo-100 text-indigo-900'
                      : 'bg-white border-gray-200 hover:border-indigo-300 hover:bg-indigo-50'
                  }`}
                >
                  <div className="text-sm font-medium">{date.getDate()}</div>
                  {dayBookings.length > 0 && (
                    <div className="text-xs text-indigo-600 mt-1">
                      {dayBookings.length} booking{dayBookings.length !== 1 ? 's' : ''}
                    </div>
                  )}
                  {isHol && (
                    <div className="text-xs text-red-600 mt-1">Holiday</div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Recent Bookings */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-5 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Recent Bookings</h3>
        </div>
        <div className="p-6">
          {bookings.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No bookings for this month
            </div>
          ) : (
            <div className="space-y-3">
              {bookings.slice(0, 10).map((booking) => (
                <div key={booking.id} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
                  <div>
                    <p className="font-medium text-gray-900">
                      {new Date(booking.bookingDate).toLocaleDateString('en-US', { 
                        weekday: 'long', 
                        month: 'long', 
                        day: 'numeric' 
                      })}
                    </p>
                    <p className="text-sm text-gray-600">
                      {booking.startTime} - {booking.endTime}
                    </p>
                    {booking.notes && (
                      <p className="text-sm text-gray-500 mt-1">{booking.notes}</p>
                    )}
                  </div>
                  <div className="flex items-center space-x-2">
                    <span className={`px-3 py-1 text-xs font-medium rounded-full ${
                      booking.status === 'confirmed'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                    }`}>
                      {booking.status}
                    </span>
                    {booking.status === 'confirmed' && (
                      <button
                        onClick={() => cancelBooking(booking.id)}
                        className="px-3 py-1 text-sm font-medium text-red-600 hover:bg-red-50 rounded-md"
                      >
                        Cancel
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Configuration Modal */}
      {showConfigModal && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">Booking Configuration</h3>
            </div>
            <form onSubmit={handleConfigSubmit} className="p-6 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Time
                  </label>
                  <input
                    type="time"
                    value={configForm.startTime}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, startTime: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Time
                  </label>
                  <input
                    type="time"
                    value={configForm.endTime}
                    onChange={(e) => setConfigForm(prev => ({ ...prev, endTime: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Slot Duration (minutes)
                </label>
                <input
                  type="number"
                  value={configForm.slotDuration}
                  onChange={(e) => setConfigForm(prev => ({ ...prev, slotDuration: parseInt(e.target.value) }))}
                  min="15"
                  step="15"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Working Days
                </label>
                <div className="grid grid-cols-7 gap-2">
                  {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                    <button
                      key={day}
                      type="button"
                      onClick={() => toggleWorkingDay(index)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        configForm.workingDays.includes(index)
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {day}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Holidays
                </label>
                <input
                  type="date"
                  onChange={(e) => {
                    if (e.target.value) {
                      addHoliday(new Date(e.target.value + 'T00:00:00'));
                      e.target.value = '';
                    }
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 mb-2"
                />
                {configForm.holidays.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {configForm.holidays.map(holiday => (
                      <span key={holiday} className="inline-flex items-center px-3 py-1 bg-red-100 text-red-800 text-sm rounded-md">
                        {new Date(holiday + 'T00:00:00').toLocaleDateString()}
                        <button
                          type="button"
                          onClick={() => removeHoliday(holiday)}
                          className="ml-2 text-red-600 hover:text-red-800"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowConfigModal(false)}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : 'Save Configuration'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Booking Modal */}
      {showBookingModal && selectedDate && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Book Slot - {selectedDate.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </h3>
            </div>
            <form onSubmit={handleBookingSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Select Time Slot
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {generateTimeSlots().map((slot, index) => {
                    const isBooked = isSlotBooked(selectedDate, slot);
                    return (
                      <button
                        key={index}
                        type="button"
                        disabled={isBooked}
                        onClick={() => setSelectedTimeSlot(slot)}
                        className={`px-4 py-3 text-sm font-medium rounded-md border-2 transition-colors ${
                          isBooked
                            ? 'bg-gray-100 border-gray-300 text-gray-400 cursor-not-allowed'
                            : selectedTimeSlot?.start === slot.start
                            ? 'bg-indigo-600 border-indigo-600 text-white'
                            : 'bg-white border-gray-300 text-gray-700 hover:border-indigo-500'
                        }`}
                      >
                        {slot.start} - {slot.end}
                        {isBooked && <span className="block text-xs mt-1">Booked</span>}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (Optional)
                </label>
                <textarea
                  value={bookingNotes}
                  onChange={(e) => setBookingNotes(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  placeholder="Add any special requirements or notes..."
                />
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowBookingModal(false);
                    setSelectedDate(null);
                    setSelectedTimeSlot(null);
                    setBookingNotes('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={!selectedTimeSlot || submitting}
                  className="px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-md hover:bg-indigo-700 disabled:opacity-50"
                >
                  {submitting ? 'Booking...' : 'Confirm Booking'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingManager;
